import { useEffect, useState } from "react";
import { HAZARD_LABEL } from "@/lib/hazard";
import { api } from "@/lib/api";

const BAND_COLOR = { internal: "bg-white/20", listed: "bg-atlas-secondary", alerted: "bg-atlas-red", rule_alert: "bg-atlas-orange" };
const Title = ({ children }) => <div className="text-[10px] uppercase tracking-widest text-atlas-cyan/60 font-mono mb-2">{children}</div>;

const useLoad = (open, fn) => {
  const [d, setD] = useState(null);
  useEffect(() => { if (open) fn().then(setD).catch(() => {}); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  return d;
};

// What the 70% public line filters out: hypothesis-score spread of current anomalies, stacked by band.
export const ConfidenceHistogram = ({ open }) => {
  const d = useLoad(open, api.confidenceHistogram);
  if (!d) return null;
  const max = Math.max(...d.bins.map((b) => b.count), 1);
  const below = d.bins.filter((b) => b.to <= d.public_line).reduce((n, b) => n + b.count, 0);
  return (
    <div className="mb-5 border border-white/10 bg-atlas-void/40 p-3" data-testid="confidence-histogram">
      <Title>Confidence spread · current anomalies</Title>
      <div className="flex items-end gap-1 h-24">
        {d.bins.map((b) => (
          <div key={b.from} className="flex-1 flex flex-col items-center justify-end h-full relative" data-testid={`hist-bin-${b.from}`}
            title={`${b.from}–${b.to}%: ${b.count} (internal ${b.internal} · listed ${b.listed} · alerted ${b.alerted} · rule ${b.rule_alert})`}>
            <span className="text-[8px] font-mono text-atlas-secondary mb-0.5">{b.count || ""}</span>
            <div className="w-full flex flex-col-reverse" style={{ height: `${(b.count / max) * 80}%` }}>
              {["internal", "rule_alert", "listed", "alerted"].map((k) => b[k] > 0 && (
                <div key={k} className={BAND_COLOR[k]} style={{ height: `${(b[k] / b.count) * 100}%` }} />
              ))}
            </div>
            {b.from === d.public_line && <div className="absolute -left-0.5 top-0 bottom-0 border-l border-dashed border-atlas-cyan" />}
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[8px] font-mono text-atlas-secondary/80 mt-1"><span>0%</span><span>70% line</span><span>100%</span></div>
      <p className="text-[9px] font-mono text-atlas-secondary mt-1" data-testid="hist-summary">
        {below} of {d.total - d.no_hypothesis} scored anomalies sit below {d.public_line}% (hypothesis score). Rule alerts can still list some of them. {d.no_hypothesis} awaiting a hypothesis.
      </p>
      <div className="flex gap-3 mt-1 text-[9px] font-mono text-atlas-secondary">
        {Object.entries(BAND_COLOR).map(([k, c]) => <span key={k} className="flex items-center gap-1"><span className={`w-2 h-2 ${c}`} />{k.replace("_", " ")}</span>)}
      </div>
    </div>
  );
};

// Agency events that landed just outside a call's window — for tuning timing, never scored as hits.
export const NearMisses = ({ open }) => {
  const d = useLoad(open, api.nearMisses);
  if (!d) return null;
  return (
    <div className="mb-5 border border-white/10 bg-atlas-void/40 p-3" data-testid="near-misses">
      <Title>Near misses · agency events just outside the window ({d.total})</Title>
      <div className="flex flex-wrap gap-2 mb-2">
        {d.summary.map((s) => (
          <span key={`${s.hazard}-${s.side}`} className="text-[9px] font-mono text-atlas-secondary" data-testid={`nm-sum-${s.hazard}-${s.side}`}>
            {HAZARD_LABEL[s.hazard] || s.hazard} · {s.side === "too_early" ? "event before flag" : "event after window"}: <span className="text-white">{s.count}</span>
          </span>
        ))}
      </div>
      {d.items.length === 0 ? (
        <p className="text-[10px] font-mono text-atlas-secondary">None yet.</p>
      ) : (
        <div className="max-h-48 overflow-y-auto">
          {d.items.slice(0, 40).map((x, i) => (
            <div key={i} className="py-1 border-b border-white/5 text-[10px] font-mono" data-testid={`near-miss-${i}`}>
              <div className="flex justify-between gap-2">
                <span className="text-white truncate">{x.region}</span>
                <span className={x.side === "too_early" ? "text-amber-300 shrink-0" : "text-sky-300 shrink-0"}>
                  {x.side === "too_early" ? `${Math.abs(x.offset_h)}h before flag` : `${x.offset_h}h after window`}
                </span>
              </div>
              <div className="text-atlas-secondary truncate">
                {x.link ? <a href={x.link} target="_blank" rel="noopener noreferrer" className="hover:text-atlas-cyan">{x.event}</a> : x.event} · {x.source} · {x.distance_km} km
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="text-[9px] text-atlas-secondary/80 mt-1">Same hazard, within matching distance, 72 h before the flag or 72 h after window + grace. Not counted as hits.</p>
    </div>
  );
};
